import { useMemo } from "react";
import { motion } from "framer-motion";
import {
  ChevronRight,
  Plus,
  HelpCircle,
  ArrowLeft,
  Shield,
  Clock,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { FortAgentSkuConfigItem } from "@/api/types";
import { C } from "@/components/modal/home";
import { getAgentTierKey, getTokenName } from "@/config/common";
import {
  formatEtherWithDecimals,
  formatKMBTruncate,
  formatThousands,
  formatUsdPrefix,
} from "@/lib/utils";
import { AGENT_IMAGE_URL_ACTIVE } from "./MyAgentsSection/constants";
import { CCC_AIF_ADDRESS } from "@/config/env";
import ScrollReveal from "@/components/ScrollReveal";
import { showInfo } from "@/lib/toast";

/** 与 forttrade-dapp 市场卡头像一致 */

/** 与 forttrade-dapp `AGENT_TYPES[].tagColor` 顺序一致；接口无 tagColor 时用列表 index 取色 */
const AGENT_MARKET_ACCENT_COLORS = [
  "#22c55e",
  "#22d3ee",
  "#d4a017",
  "#f59e0b",
  "#ef4444",
] as const;

function getAgentMarketAccentColor(index: number): string {
  return AGENT_MARKET_ACCENT_COLORS[index % AGENT_MARKET_ACCENT_COLORS.length];
}

function AgentMarketCard({
  agent,
  index,
  onBuy,
  onDetail,
}: {
  agent: FortAgentSkuConfigItem;
  index: number;
  onBuy: () => void;
  onDetail: () => void;
}) {
  const { t } = useTranslation();
  // const soldCount = agent.sellCount;
  const soldCount = +agent.supplyCap - +agent.leftSupplyCount;
  const soldPct = (soldCount / agent.supplyCap) * 100;
  const isSoldOut = agent.leftSupplyCount <= 0;
  const accent = getAgentMarketAccentColor(index);

  const totalValue = useMemo(() => {
    return formatEtherWithDecimals(
      BigInt(agent.sellPriceUsdt) * BigInt(agent.supplyCap),
      0
    );
  }, [agent.sellPriceUsdt, agent.supplyCap]);
  const handleBuyClick = () => {
    if (agent.name === "PRO 4" || agent.name === "PRO 5") {
      showInfo(t("homePage.agentCard.purchaseRestrictedProTier"), {
        duration: 4000,
      });
      return;
    }
    onBuy();
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="rounded-xl overflow-hidden relative"
      style={{
        background: C.card,
        border: `1px solid ${isSoldOut ? `${C.red}30` : `${accent}30`}`,
        filter: isSoldOut ? "grayscale(0.6)" : "none",
      }}
    >
      {!isSoldOut && (
        <div
          className="h-0.5 w-full"
          style={{
            background: `linear-gradient(90deg, ${accent}60, transparent)`,
          }}
        />
      )}
      {isSoldOut && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center cursor-not-allowed"
          style={{ background: "rgba(10,11,14,0.60)" }}
          onClick={e => {
            e.stopPropagation();
            toast(t("homePage.agentCard.soldOutToast"));
          }}
        >
          <div
            className="flex flex-col items-center gap-2"
            style={{ transform: "rotate(-12deg)" }}
          >
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center"
              style={{ border: `3px solid ${C.red}`, background: `${C.red}15` }}
            >
              <span
                className="text-lg font-black tracking-widest"
                style={{ color: C.red }}
              >
                {t("homePage.agentCard.soldOut")}
              </span>
            </div>
            <span
              className="text-[10px] font-semibold tracking-wider"
              style={{ color: `${C.red}99` }}
            >
              SOLD OUT
            </span>
          </div>
        </div>
      )}

      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center relative"
              style={{
                background: `linear-gradient(135deg, ${"#22c55e"}15, ${"#22c55e"}08)`,
                border: `1px solid ${"#22c55e"}20`,
              }}
            >
              <img
                src={AGENT_IMAGE_URL_ACTIVE}
                alt=""
                className="w-8 h-8 object-contain"
              />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-bold">{agent.name}</span>
                <span
                  className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                  style={{
                    background: `${accent}12`,
                    color: accent,
                    border: `1px solid ${accent}20`,
                  }}
                >
                  {t(getAgentTierKey(agent.name))}
                </span>
              </div>
              <div className="text-[10px] mt-0.5" style={{ color: C.dim }}>
                {t("homePage.agentCard.limited", {
                  count: agent.supplyCap,
                  value: formatKMBTruncate(totalValue, 0),
                })}
              </div>
            </div>
          </div>

          <div className="text-right">
            <div
              className="text-lg font-black mono-num"
              style={{
                color: C.text,
              }}
            >
              {formatUsdPrefix(
                formatThousands(
                  formatEtherWithDecimals(agent.sellPriceUsdt, 0),
                  0
                )
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-1.5 mb-3">
          {[
            {
              label: t("homePage.benefit.custodyBoost"),
              value: `${agent.aiManageAddSpeed / 100}%`,
              color: C.primary,
            },
            {
              label: t("homePage.benefit.releaseMultiple"),
              value: `${agent.exitMultiple / 100}x`,
              color: C.green,
            },
            {
              label: t("homePage.benefit.aifToken", {
                tokenNameAif: getTokenName(CCC_AIF_ADDRESS),
              }),
              value: formatUsdPrefix(
                formatThousands(
                  formatEtherWithDecimals(agent.aifValueUsdt, 0),
                  0
                )
              ),
              color: C.amber,
            },
            // {
            //   label: t("homePage.benefit.teamLevel"),
            //   value: `C${agent.giveLevel}`,
            //   color: C.cyan,
            // },
          ].map(b => (
            <div
              key={b.label}
              className="text-center py-1.5 rounded-lg"
              style={{
                background: `${b.color}06`,
                border: `1px solid ${b.color}10`,
              }}
            >
              <div className="text-[10px]" style={{ color: C.dim }}>
                {b.label}
              </div>
              <div
                className="text-xs font-bold mono-num"
                style={{ color: b.color }}
              >
                {b.value}
              </div>
            </div>
          ))}
        </div>

        <div className="mb-3">
          <div className="flex justify-between text-[10px] mb-1">
            <span style={{ color: C.dim }}>
              {t("homePage.agentCard.soldProgress", {
                sold: soldCount,
                total: agent.supplyCap,
              })}
            </span>
            <span
              className="mono-num font-semibold"
              style={{ color: soldPct > 80 ? C.red : C.muted }}
            >
              {soldPct.toFixed(0)}%
            </span>
          </div>
          <div
            className="h-1.5 rounded-full overflow-hidden"
            style={{ background: "rgba(255,255,255,0.04)" }}
          >
            <div
              className={`h-full rounded-full transition-all duration-1000 progress-glow ${soldPct > 80 ? "progress-glow-danger" : ""}`}
              style={{
                width: `${soldPct}%`,
                background:
                  soldPct > 80
                    ? `linear-gradient(90deg, ${C.amber}, ${C.red})`
                    : accent,
              }}
            />
          </div>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            className="flex-1 py-2.5 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 btn-primary"
            style={
              isSoldOut
                ? {
                    opacity: 0.4,
                    cursor: "not-allowed",
                    pointerEvents: "none" as const,
                  }
                : {}
            }
            onClick={isSoldOut ? undefined : handleBuyClick}
          >
            <Plus size={12} />
            {isSoldOut
              ? t("homePage.agentCard.alreadySoldOut")
              : t("homePage.agentCard.buyNow")}
          </button>
          <button
            type="button"
            className="px-3 py-2.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-all"
            style={
              isSoldOut
                ? {
                    background: "rgba(255,255,255,0.02)",
                    border: `1px solid ${C.border}`,
                    color: C.dim,
                    opacity: 0.4,
                    cursor: "not-allowed",
                    pointerEvents: "none" as const,
                  }
                : {
                    background: "rgba(255,255,255,0.03)",
                    border: `1px solid ${C.border}`,
                    color: C.muted,
                  }
            }
            onClick={isSoldOut ? undefined : onDetail}
          >
            {t("homePage.agentCard.detail")}
            <ChevronRight size={12} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export type AgentMarketSectionProps = {
  variant: "home" | "standalone";
  skuLoading: boolean;
  skus: FortAgentSkuConfigItem[] | undefined;
  marketSoldTotal: number;
  marketSupplyCap: number;
  soldPct: number;
  onOpenBuyNotice: () => void;
  onBuy: (agent: FortAgentSkuConfigItem) => void;
  onDetail: (agent: FortAgentSkuConfigItem) => void;
};

export function AgentMarketSection({
  variant,
  skuLoading,
  skus,
  marketSoldTotal,
  marketSupplyCap,
  soldPct,
  onOpenBuyNotice,
  onBuy,
  onDetail,
}: AgentMarketSectionProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isStandalone = variant === "standalone";

  const loadedMarketStatsProgress = (
    <>
      <div className="grid grid-cols-3 gap-2 mb-2">
        <div
          className="p-2.5 rounded-lg"
          style={{
            background: "rgba(12,16,32,0.5)",
            border: "1px solid rgba(100,140,200,0.06)",
          }}
        >
          <div className="text-[10px] mb-1" style={{ color: C.dim }}>
            {t("homePage.market.totalSupply")}
          </div>
          <div className="text-sm font-black mono-num">{marketSupplyCap}</div>
        </div>
        <div
          className="p-2.5 rounded-lg"
          style={{
            background: "rgba(12,16,32,0.5)",
            border: "1px solid rgba(100,140,200,0.06)",
          }}
        >
          <div className="text-[10px] mb-1" style={{ color: C.dim }}>
            {t("homePage.market.soldTotal")}
          </div>
          <div
            className="text-sm font-black mono-num"
            style={{ color: C.primary }}
          >
            {marketSoldTotal}
          </div>
        </div>
        <div
          className="p-2.5 rounded-lg"
          style={{
            background: "rgba(12,16,32,0.5)",
            border: "1px solid rgba(100,140,200,0.06)",
          }}
        >
          <div className="text-[10px] mb-1" style={{ color: C.dim }}>
            {t("homePage.market.saleProgress")}
          </div>
          <div
            className="text-sm font-black mono-num"
            style={{ color: soldPct > 80 ? C.red : C.amber }}
          >
            {soldPct.toFixed(0)}%
          </div>
        </div>
      </div>
      <div
        className="h-2 rounded-full overflow-hidden"
        style={{ background: "rgba(255,255,255,0.04)" }}
      >
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${soldPct}%` }}
          transition={{ duration: 1, ease: "easeOut" }}
          className={`h-full rounded-full progress-glow ${soldPct > 80 ? "progress-glow-danger" : ""}`}
          style={{
            background:
              soldPct > 80
                ? `linear-gradient(90deg, ${C.amber}, ${C.red})`
                : `linear-gradient(90deg, ${C.primary}, #34d399)`,
          }}
        />
      </div>
    </>
  );

  const inner = (
    <>
      {!isStandalone && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className="w-0.5 h-4 rounded-full"
              style={{
                background: "linear-gradient(180deg, #2196f6, #22c55e)",
              }}
            />
            <h2 className="font-bold text-lg leading-tight">
              {t("homePage.market.title")}
            </h2>
            <button
              type="button"
              className="w-5 h-5 rounded-full flex items-center justify-center transition-all"
              style={{
                background: "rgba(100,140,200,0.04)",
                border: "1px solid rgba(100,140,200,0.06)",
              }}
              onClick={() => onOpenBuyNotice()}
            >
              <HelpCircle size={11} style={{ color: C.dim }} />
            </button>
          </div>
          <div className="flex items-center gap-2 shrink-0 text-[10px]">
            <span className="truncate" style={{ color: C.dim }}>
              {t("homePage.market.subtitle")}
            </span>
            {/* <button
              type="button"
              className="font-semibold whitespace-nowrap hover:opacity-90"
              style={{ color: C.primary }}
              onClick={() => navigate("/market")}
            >
              {t("homePage.market.goFullPage")}
            </button> */}
          </div>
        </div>
      )}

      {skuLoading ? (
        <>
          <div className="grid grid-cols-3 gap-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="p-2.5 rounded-lg animate-pulse min-h-[52px]"
                style={{
                  background: "rgba(12,16,32,0.5)",
                  border: "1px solid rgba(100,140,200,0.06)",
                }}
              />
            ))}
          </div>
          <div
            className="h-2 rounded-full overflow-hidden animate-pulse"
            style={{ background: "rgba(255,255,255,0.04)" }}
          />
        </>
      ) : isStandalone ? (
        loadedMarketStatsProgress
      ) : (
        <ScrollReveal direction="up" distance={16} delay={0.05} duration={0.45}>
          {loadedMarketStatsProgress}
        </ScrollReveal>
      )}

      <div className="space-y-3">
        {skuLoading
          ? Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="h-36 rounded-xl animate-pulse"
                style={{ background: C.card }}
              />
            ))
          : skus?.map((agent, i) =>
              isStandalone ? (
                <AgentMarketCard
                  key={agent.skuId}
                  agent={agent}
                  index={i}
                  onBuy={() => onBuy(agent)}
                  onDetail={() => onDetail(agent)}
                />
              ) : (
                <ScrollReveal
                  key={agent.skuId}
                  direction="up"
                  distance={20}
                  delay={i * 0.08}
                  duration={0.45}
                  threshold={0.1}
                >
                  <AgentMarketCard
                    agent={agent}
                    index={i}
                    onBuy={() => onBuy(agent)}
                    onDetail={() => onDetail(agent)}
                  />
                </ScrollReveal>
              )
            )}
      </div>

      {isStandalone && (
        <div
          className="mt-6 p-4 rounded-lg text-center"
          style={{ background: C.card, border: `1px solid ${C.border}` }}
        >
          <div className="text-xs space-y-1.5" style={{ color: C.dim }}>
            <div className="flex items-center justify-center gap-1.5">
              <Shield size={10} style={{ color: C.primary }} />
              <span>{t("marketPage.footerOnChain")}</span>
            </div>
            <div className="flex items-center justify-center gap-1.5">
              <Clock size={10} />
              <span>{t("marketPage.footerSchedule")}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (isStandalone) {
    return (
      <div className="space-y-4 relative z-10">
        <div className="mb-2">
          <button
            type="button"
            className="flex items-center gap-1.5 text-xs font-medium mb-4 transition-all hover:opacity-80"
            style={{ color: C.muted }}
            onClick={() => navigate("/")}
          >
            <ArrowLeft size={14} />
            {t("marketPage.backHome")}
          </button>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-black">
                {t("homePage.market.title")}
              </h1>
              <button
                type="button"
                className="w-5 h-5 rounded-full flex items-center justify-center transition-all hover:opacity-80"
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: `1px solid ${C.border}`,
                }}
                onClick={() => onOpenBuyNotice()}
              >
                <HelpCircle size={11} style={{ color: C.dim }} />
              </button>
            </div>
          </div>
          <p className="text-sm leading-relaxed" style={{ color: C.dim }}>
            {t("marketPage.lead")}
          </p>
        </div>
        {inner}
      </div>
    );
  }

  return <div className="space-y-2 relative z-10">{inner}</div>;
}
